import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  Check,
  PenNib,
  Phone,
} from "@phosphor-icons/react";
import { SiteShell, useSite } from "@/components/site/SiteShell";
import { API } from "@/lib/api";
import { SITE_IMG } from "@/lib/siteImages";
import { siteImg, usePageHead } from "@/lib/siteContent";

const PERKS = [
  "Every slab photographed on the rack — pick the exact piece online or in person",
  "Holds on the slab you choose while the client decides",
  "Shop drawings, 3D previews and seam layouts on the real slab photo",
  "Vein-matched waterfalls, mitred edges and book-matched seams",
  "A client portal for quotes, drawings and approvals in one place",
  "Remnant offcuts priced for vanities, hearths and shelves",
];

const ROOF = [
  {
    title: "Extensive slab selection",
    body: "Browse our curated inventory of granite, quartz, quartzite, marble and more in person, so you and your client can see and touch exactly what is going into the home.",
  },
  {
    title: "In-house fabrication",
    body: "Our own craftsmen cut, shape and finish every piece in our facility, giving you full quality control and faster turnaround.",
  },
  {
    title: "Direct supplier relationships",
    body: "We source materials directly, which means better pricing and access to unique, high-quality stone you will not find elsewhere.",
  },
  {
    title: "Professional installation",
    body: "Our own experienced installers ensure a precise, clean fit — no third-party crews, no finger-pointing if something needs adjusting.",
  },
  {
    title: "One team, one vision",
    body: "Because every phase is managed under one roof, your project stays on track, on budget and exactly the way you envisioned it.",
  },
];

export default function SiteDesigners() {
  const site = useSite();
  const shop = site?.shop || {};
  const [slabs, setSlabs] = useState([]);

  useEffect(() => {
    fetch(`${API}/public/showroom`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then((d) =>
        setSlabs(
          (d.items || [])
            .filter((i) => i.photo && i.kind !== "Remnant")
            .slice(0, 6),
        ),
      )
      .catch(() => setSlabs([]));
  }, []);

  usePageHead({
    title: "Designers",
    description:
      "Premium stone for interior designers who lead with vision. Photographed slab inventory, holds, shop drawings and in-house fabrication and installation under one roof.",
  });

  return (
    <SiteShell site={site} testId="site-designers">
      <section className="relative min-h-[56vh] overflow-hidden sm:min-h-[66vh] lg:min-h-[78vh]">
        <img
          src={siteImg(site?.config?.designers?.image, SITE_IMG.kitchen)}
          alt="Designer kitchen with natural stone"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <span className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/40" />
        <div className="relative flex min-h-[56vh] flex-col justify-end px-6 pb-12 pt-12 sm:min-h-[66vh] sm:pb-16 sm:pt-16 lg:min-h-[78vh] lg:px-12">
          <div className="s-micro flex items-center gap-2 text-white/70">
            <PenNib size={14} weight="bold" /> Trade · Designers
          </div>
          <h1
            className="s-display mt-5 max-w-4xl text-4xl text-white sm:mt-6 sm:text-6xl lg:text-7xl"
            data-testid="designers-headline"
          >
            Premium stone for designers who lead with vision
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-white/85 sm:text-xl">
            Design support without design limits. You bring the vision — we
            bring it to life without diluting the concept or slowing the
            process.
          </p>
          <div className="mt-9 flex flex-wrap gap-3">
            <Link
              to="/contact?topic=designer"
              className="s-btn"
              data-testid="designers-hero-cta"
            >
              Book a private showroom visit
            </Link>
            <Link to="/inventory" className="s-ghost-light">
              Browse the racks
            </Link>
          </div>
        </div>
      </section>

      <section className="bg-[color:var(--s-surface)] px-6 py-16 sm:py-24 lg:px-12 lg:py-28">
        <div className="grid gap-12 lg:grid-cols-[1fr_1fr] lg:gap-20">
          <div>
            <div className="s-micro text-[color:var(--s-gold)]">
              A stone partner who keeps up
            </div>
            <h2 className="s-display mt-5 max-w-xl text-3xl sm:mt-6 sm:text-4xl lg:text-5xl">
              Creative freedom, backed by precision
            </h2>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-[color:var(--s-mute)] sm:text-lg">
              We work with designers who know exactly what they are looking for.
              We collaborate closely — offering insight when it is helpful and
              staying out of the way when it is not.
            </p>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-[color:var(--s-mute)]">
              With deep material knowledge and a respect for form and function,
              we match your aesthetic goals with surfaces that perform. The
              result is creative freedom with the execution to support it.
            </p>
          </div>
          <div>
            <div className="s-micro text-[color:var(--s-mute)]">
              What you get as a designer
            </div>
            <ul className="mt-6" data-testid="designers-perks">
              {PERKS.map((p) => (
                <li
                  key={p}
                  className="flex gap-4 border-t border-[color:var(--s-line)] py-4 text-sm text-[color:var(--s-ink)] last:border-b"
                >
                  <Check
                    size={16}
                    weight="bold"
                    className="mt-0.5 shrink-0 text-[color:var(--s-gold)]"
                  />
                  {p}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section
        className="border-t border-[color:var(--s-line)] bg-[color:var(--s-bg)] px-6 py-16 sm:py-24 lg:px-12 lg:py-28"
        data-testid="designers-roof"
      >
        <div className="max-w-3xl">
          <div className="s-micro text-[color:var(--s-gold)]">
            All under one roof
          </div>
          <h2 className="s-display mt-5 text-3xl sm:mt-6 sm:text-4xl lg:text-5xl">
            Your complete stone experience
          </h2>
          <p className="mt-6 text-base leading-relaxed text-[color:var(--s-mute)] sm:text-lg">
            From the moment you walk through our doors to the final
            installation, we handle every step so you do not have to. No
            juggling vendors, no miscommunication between teams and no surprises
            — a smooth journey from slab to finished room.
          </p>
        </div>
        <div className="mt-12 grid gap-px bg-[color:var(--s-line)] sm:grid-cols-2 lg:grid-cols-5">
          {ROOF.map((r, i) => (
            <div
              key={r.title}
              data-testid={`designers-roof-${i + 1}`}
              className="flex flex-col bg-[color:var(--s-surface)] p-6 lg:p-7"
            >
              <span className="s-num text-2xl text-[color:var(--s-gold)]">
                {String(i + 1).padStart(2, "0")}
              </span>
              <h3 className="s-display mt-4 text-xl">{r.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-[color:var(--s-mute)]">
                {r.body}
              </p>
            </div>
          ))}
        </div>
        <Link
          to="/contact?topic=designer"
          className="s-link mt-10 inline-flex"
          data-testid="designers-talk-cta"
        >
          Let&apos;s talk about your next project{" "}
         
        </Link>
      </section>

      <section className="grid gap-px bg-[color:var(--s-line)] lg:grid-cols-2">
        <div className="s-fig relative min-h-[40vh] overflow-hidden sm:min-h-[50vh] lg:min-h-full">
          <img
            src={SITE_IMG.warehouse}
            alt="Full slabs on display"
            className="absolute inset-0 h-full w-full object-cover"
          />
        </div>
        <div className="flex flex-col justify-center bg-[color:var(--s-surface)] px-6 py-14 sm:py-20 lg:px-16">
          <div className="s-micro text-[color:var(--s-gold)]">
            Curated stone, ready to work
          </div>
          <h2 className="s-display mt-5 text-3xl sm:mt-6 sm:text-4xl lg:text-5xl">
            Never design blind
          </h2>
          <p className="mt-5 max-w-md text-base leading-relaxed text-[color:var(--s-mute)]">
            Every slab on our floor is hand-selected for its movement, color,
            finish and visual story — from timeless marble to bold porcelain
            panels. With full slabs in stock and photographed online, you are
            never waiting on materials to land.
          </p>
          {slabs.length > 0 && (
            <div
              className="mt-8 grid grid-cols-3 gap-2"
              data-testid="designers-slab-strip"
            >
              {slabs.map((s) => (
                <Link
                  key={s.key}
                  to={`/inventory/${s.key}`}
                  data-testid={`designers-slab-${s.key}`}
                  title={s.color}
                  className="s-fig group relative aspect-square overflow-hidden bg-[color:var(--s-bg)]"
                >
                  <img
                    src={`${API}/public/showroom/photo/${s.thumb || s.photo}?w=700`}
                    alt={s.color}
                    className="h-full w-full object-cover"
                  />
                  <span className="absolute inset-x-0 bottom-0 truncate bg-gradient-to-t from-black/70 to-transparent px-3 pb-2 pt-6 text-xs text-white opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                    {s.color}
                  </span>
                </Link>
              ))}
            </div>
          )}
          <Link
            to="/inventory"
            className="s-link mt-8 self-start"
            data-testid="designers-inventory-cta"
          >
            See the full inventory
          </Link>
        </div>
      </section>

      <section
        className="bg-[color:var(--s-ink)] px-6 py-16 text-white sm:py-24 lg:px-12 lg:py-28"
        data-testid="designers-cta"
      >
        <div className="flex flex-col gap-10 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <div className="s-micro text-[color:var(--s-gold)]">
              Let&apos;s build something beautiful
            </div>
            <h2 className="s-display mt-5 max-w-2xl text-3xl sm:mt-6 sm:text-4xl lg:text-5xl">
              Book a private showroom visit, request slab previews or talk to us
              about an upcoming project.
            </h2>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/contact?topic=designer"
              className="s-btn !bg-[color:var(--s-surface)] !text-[color:var(--s-ink)]"
              data-testid="designers-partner-cta"
            >
              Partner with us today <ArrowUpRight size={13} weight="bold" />
            </Link>
            {shop.phone && (
              <a
                href={`tel:${shop.phone_link || shop.phone}`}
                className="s-ghost-light"
                data-testid="designers-phone"
              >
                <Phone size={14} weight="bold" /> {shop.phone}
              </a>
            )}
          </div>
        </div>
      </section>
    </SiteShell>
  );
}
